import { supabase } from "@/integrations/supabase/client";

const STORAGE_OBJECT_URL_RE =
  /\/storage\/v1\/object\/(?:public|sign\/[^/]+)\/([^/]+)\/(.+)$/;

/** Extrai bucket e path de URLs públicas ou assinadas do Supabase Storage. */
export function parseSupabaseStorageUrl(
  url: string
): { bucket: string; path: string } | null {
  if (!url?.trim()) return null;
  try {
    const parsed = new URL(url.trim());
    const match = parsed.pathname.match(STORAGE_OBJECT_URL_RE);
    if (!match) return null;
    return {
      bucket: decodeURIComponent(match[1]),
      path: decodeURIComponent(match[2]),
    };
  } catch {
    return null;
  }
}

/** Gera URL acessível (signed) para buckets privados; URLs externas passam direto. */
export async function getAccessibleStorageUrl(
  url: string,
  expiresIn = 3600
): Promise<string> {
  const trimmed = url?.trim();
  if (!trimmed) return url;

  const parsed = parseSupabaseStorageUrl(trimmed);
  if (!parsed) return trimmed;

  const { data, error } = await supabase.storage
    .from(parsed.bucket)
    .createSignedUrl(parsed.path, expiresIn);

  if (error || !data?.signedUrl) {
    console.warn("Falha ao gerar signed URL:", error?.message ?? trimmed);
    return trimmed;
  }
  return data.signedUrl;
}

export async function openStorageUrl(url: string): Promise<void> {
  const accessible = await getAccessibleStorageUrl(url);
  window.open(accessible, "_blank", "noopener,noreferrer");
}

const DEFAULT_UPLOAD_BUCKETS = [
  "lovable-uploads",
  "uploads",
  "documents",
  "public",
] as const;

export type StorageUploadResult = {
  bucket: string;
  path: string;
  /** Formato legado salvo no banco — resolver com getAccessibleStorageUrl ao exibir */
  publicUrl: string;
};

/**
 * Prefixa o path com o uid do usuário (LGPD / ownership no Storage).
 * Idempotente se o path já começar com o uid.
 */
export function withUserStoragePrefix(
  filePath: string,
  userId: string | null | undefined
): string {
  const cleaned = String(filePath || "").replace(/^\/+/, "");
  if (!cleaned || !userId) return cleaned;
  if (cleaned === userId || cleaned.startsWith(`${userId}/`)) return cleaned;
  return `${userId}/${cleaned}`;
}

/** Upload com fallback entre buckets; path prefixado com auth.uid() quando logado. */
export async function uploadFileToStorage(
  filePath: string,
  file: File,
  options?: {
    buckets?: readonly string[];
    upsert?: boolean;
    cacheControl?: string;
    /** Se false, não prefixa com uid (legado). Default: true. */
    prefixUserId?: boolean;
  }
): Promise<StorageUploadResult> {
  const buckets = options?.buckets ?? DEFAULT_UPLOAD_BUCKETS;
  let lastError: unknown = null;

  let resolvedPath = String(filePath || "").replace(/^\/+/, "");
  if (options?.prefixUserId !== false) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    resolvedPath = withUserStoragePrefix(resolvedPath, user?.id);
  }

  for (const bucket of buckets) {
    try {
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(resolvedPath, file, {
          cacheControl: options?.cacheControl ?? "3600",
          upsert: options?.upsert ?? false,
        });
      if (uploadError) {
        lastError = uploadError;
        continue;
      }
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(resolvedPath);
      if (urlData?.publicUrl) {
        return { bucket, path: resolvedPath, publicUrl: urlData.publicUrl };
      }
    } catch (e) {
      lastError = e;
    }
  }

  throw new Error(
    `Falha no upload: ${
      lastError instanceof Error ? lastError.message : "Erro desconhecido"
    }`
  );
}
